import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  IconButton,
  Avatar,
  Typography,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import ClearIcon from "@mui/icons-material/Clear";
import {
  ChatContainer,
  MessagesContainer,
  MessageRow,
  MessageBubble,
  InputContainer,
  EmptyChatPlaceholder,
} from "./ChatInterface.styles";
import { chatApiMCP, clearChatHistoryMCP } from "../../api/api";
import { useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";

export default function ChatInterface() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [autoSent, setAutoSent] = useState(false);
  const [typing, setTyping] = useState(false);
  const [thinkingDots, setThinkingDots] = useState(0); // For animated dots
  const [sessionId, setSessionId] = useState(
    () => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  );
  const location = useLocation();

  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, typing]);

  // Animate thinking dots (bouncing animation)
  useEffect(() => {
    if (!typing) {
      setThinkingDots(0);
      return;
    }

    const interval = setInterval(() => {
      setThinkingDots((prev) => (prev + 1) % 4); // 0, 1, 2, 3 then back to 0
    }, 200); // 0.2 seconds

    return () => clearInterval(interval);
  }, [typing]);

  const handleClearChat = async () => {
    if (messages.length === 0) return;
    
    // Clear local messages first
    setMessages([]);
    setInput("");
    setAutoSent(false);
    
    // Clear backend conversation history
    try {
      await clearChatHistoryMCP(sessionId);
    } catch (err) {
      console.log("Note: Backend clear failed, but continuing with new session");
    }
    
    // Generate new session ID for fresh start
    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);
    
    // Dispatch event to reset products and filters on the right side
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("chat:clear", {
          detail: {
            sessionId: newSessionId,
          },
          bubbles: true,
          cancelable: true,
        })
      );
    }
  };

  const push = (sender, text) =>
    setMessages((prev) => [...prev, { sender, text }]);

  const handleSend = async (text = null) => {
    const messageText = text ?? input;
    if (!messageText.trim()) return;
    setInput("");

    push("user", messageText);
    setTyping(true);

    try {
      const res = await chatApiMCP(messageText, sessionId);
      const aiText = res?.response ?? "No response received.";
      push("ai", aiText);

      if (typeof window !== "undefined") {
        const products = res?.products || [];
        const eventData = {
          filters: res?.filters || {},
          query: messageText,
          sessionId: sessionId,
          products: products,
        };

        window.dispatchEvent(
          new CustomEvent("mcp:products", {
            detail: eventData,
            bubbles: true,
            cancelable: true,
          })
        );
      }
    } catch (err) {
      console.error(err);
      push("ai", "Failed to get response from server.");
    } finally {
      setTyping(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchQuery = params.get("search") || "";
    if (searchQuery && !autoSent) {
      handleSend(searchQuery);
      setAutoSent(true);
    }
  }, [location.search, autoSent]);

  return (
    <ChatContainer elevation={3}>
      <MessagesContainer>
        {messages.length === 0 ? (
          <EmptyChatPlaceholder>
            <img src="/PDIMascot.png" alt="Mascot" />
            <span>How can I be of assistance?</span>
          </EmptyChatPlaceholder>
        ) : (
          messages.map((msg, idx) => (
            <MessageRow key={idx} sender={msg.sender}>
              {msg.sender === "user" && (
                <Avatar sx={{ mr: 1, bgcolor: "grey.400" }}>U</Avatar>
              )}
              <MessageBubble sender={msg.sender}>
                {msg.sender === "ai" ? (
                  <Typography
                    variant="body2"
                    component="div"
                    sx={{
                      "& p": { m: 0, mb: 1 },
                      "& p:last-child": { mb: 0 },
                      "& ul, & ol": { m: 0, mb: 1, pl: 2.5 },
                      "& li": { mb: 0.5 },
                      "& strong": { fontWeight: 600 },
                      "& code": {
                        px: 0.5,
                        borderRadius: 0.5,
                        bgcolor: "rgba(0,0,0,0.06)",
                        fontSize: "0.85em",
                      },
                    }}
                  >
                    <ReactMarkdown>{msg.text}</ReactMarkdown>
                  </Typography>
                ) : (
                  <Typography variant="body2">{msg.text}</Typography>
                )}
              </MessageBubble>
              {msg.sender === "ai" && (
                <Avatar sx={{ ml: 1 }} src="/AiAvatar.png" alt="Akini" />
              )}
            </MessageRow>
          ))
        )}

        {typing && (
          <MessageRow sender="ai">
            <MessageBubble
              sender="ai"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <CircularProgress size={16} />
              <Typography variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                Akini is thinking
                <span style={{ 
                  display: "inline-flex", 
                  alignItems: "baseline",
                  marginLeft: "4px",
                  minWidth: "24px",
                  height: "1em"
                }}>
                  <span style={{ 
                    display: "inline-block",
                    transform: thinkingDots === 1 ? "translateY(-4px)" : "translateY(0)",
                    transition: "transform 0.2s ease-in-out"
                  }}>.</span>
                  <span style={{ 
                    display: "inline-block",
                    transform: thinkingDots === 2 ? "translateY(-4px)" : "translateY(0)",
                    transition: "transform 0.2s ease-in-out"
                  }}>.</span>
                  <span style={{ 
                    display: "inline-block",
                    transform: thinkingDots === 3 ? "translateY(-4px)" : "translateY(0)",
                    transition: "transform 0.2s ease-in-out"
                  }}>.</span>
                </span>
              </Typography>
            </MessageBubble>
            <Avatar sx={{ ml: 1 }} src="/AiAvatar.png" alt="Akini" />
          </MessageRow>
        )}

        <div ref={messagesEndRef} />
      </MessagesContainer>

      <InputContainer>
        <TextField
          variant="outlined"
          placeholder="Type here..."
          size="small"
          fullWidth
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleSend()}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  size="small"
                  onClick={handleClearChat}
                  sx={{ 
                    mr: 0.5,
                    color: messages.length > 0 ? "action.active" : "action.disabled"
                  }}
                  disabled={messages.length === 0}
                  title="Clear chat history"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <IconButton color="primary" onClick={() => handleSend()} sx={{ ml: 1 }}>
          <SendIcon />
        </IconButton>
      </InputContainer>
    </ChatContainer>
  );
}
